import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { PendingComment } from '../services/api';
import AuroraBackground from '../components/AuroraBackground';
import { decodeAccountId } from '../services/common';

const QueueItemPage: React.FC = () => {
    const { accountId, queueItemId } = useParams<{ accountId: string; queueItemId: string }>();
    const navigate = useNavigate();
    const [comment, setComment] = useState<PendingComment | null>(null);
    const [editedContent, setEditedContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const hasChanges = comment && editedContent !== comment.content;

    useEffect(() => {
        const fetchComment = async () => {
            if (!accountId || !queueItemId) return;

            try {
                const data = await api.getQueueComment(accountId, queueItemId);
                setComment(data);
                setEditedContent(data.content);
            } catch (err) {
                console.error('Failed to fetch queue comment:', err);
                setError('Failed to load comment');
            } finally {
                setLoading(false);
            }
        };

        fetchComment();
    }, [accountId, queueItemId]);

    const handleSave = async () => {
        if (!accountId || !queueItemId || !hasChanges) return;

        setSaving(true);
        setSaveSuccess(false);
        try {
            const updated = await api.updateQueueComment(accountId, queueItemId, editedContent);
            setComment(updated);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 2000);
        } catch (err) {
            console.error('Failed to save comment:', err);
            setError('Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return 'Unknown';
        const date = new Date(dateStr);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    if (loading) {
        return (
            <>
                <AuroraBackground />
                <div
                    style={{
                        minHeight: '100vh',
                        paddingTop: '72px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <div style={{ textAlign: 'center', color: '#666666' }}>
                        <div
                            style={{
                                width: '32px',
                                height: '32px',
                                border: '3px solid rgba(0, 0, 0, 0.1)',
                                borderTop: '3px solid #7C3AED',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                margin: '0 auto 16px',
                            }}
                        />
                        Loading comment...
                    </div>
                    <style>
                        {`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}
                    </style>
                </div>
            </>
        );
    }

    if (error || !comment) {
        return (
            <>
                <AuroraBackground />
                <div
                    style={{
                        minHeight: '100vh',
                        paddingTop: '72px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#EF4444', fontSize: '18px', marginBottom: '16px' }}>
                            {error || 'Comment not found'}
                        </div>
                        <button
                            onClick={() => navigate(-1)}
                            style={{
                                padding: '12px 24px',
                                background: '#7C3AED',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 600,
                            }}
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <AuroraBackground />
            <div
                style={{
                    minHeight: '100vh',
                    paddingTop: '72px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                }}
            >
            <div style={{ maxWidth: '640px', margin: '0 auto', padding: '32px 24px' }}>
                {/* Back Navigation */}
                <button
                    onClick={() => navigate(-1)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#666666',
                        fontSize: '14px',
                        marginBottom: '24px',
                        padding: '8px 0',
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    Back to Queue
                </button>

                {/* Queue Item Card */}
                <div
                    style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        overflow: 'hidden',
                    }}
                >
                    {/* Header with status */}
                    <div
                        style={{
                            padding: '20px 24px',
                            borderBottom: '1px solid #E5E5E5',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <div>
                            <h1 style={{ fontSize: '18px', fontWeight: 600, color: '#000000', margin: '0 0 4px 0' }}>
                                Queue Item
                            </h1>
                            <div style={{ fontSize: '13px', color: '#666666' }}>
                                Queued: {formatDate(comment.queuedAt)}
                            </div>
                        </div>
                        <span
                            style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                padding: '4px 12px',
                                borderRadius: '4px',
                                background: comment.status === 'pending' ? '#FEF3C7' : '#D1FAE5',
                                color: comment.status === 'pending' ? '#92400E' : '#065F46',
                                textTransform: 'uppercase',
                            }}
                        >
                            {comment.status}
                        </span>
                    </div>

                    {/* Post Info */}
                    <div style={{ padding: '20px 24px', borderBottom: '1px solid #E5E5E5' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#666666', textTransform: 'uppercase', marginBottom: '8px' }}>
                            Target Post
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    background: '#F59E0B',
                                    color: '#FFFFFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                }}
                            >
                                {(comment.username || 'U').charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>
                                    @{comment.username || 'unknown'}
                                </div>
                                <a
                                    href={comment.postUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        fontSize: '12px',
                                        color: '#3B82F6',
                                        textDecoration: 'none',
                                    }}
                                >
                                    View on Instagram
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Editable Comment Content */}
                    <div style={{ padding: '20px 24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#666666', textTransform: 'uppercase' }}>
                                Comment Content
                            </div>
                            {hasChanges && (
                                <div style={{ fontSize: '11px', color: '#F59E0B', fontWeight: 500 }}>
                                    Unsaved changes
                                </div>
                            )}
                        </div>
                        <textarea
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            style={{
                                width: '100%',
                                minHeight: '120px',
                                padding: '14px',
                                fontSize: '14px',
                                lineHeight: 1.6,
                                border: '1px solid #E5E5E5',
                                borderRadius: '8px',
                                resize: 'vertical',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                            }}
                            placeholder="Enter your comment..."
                        />
                        <div style={{ fontSize: '11px', color: '#999999', marginTop: '8px', textAlign: 'right' }}>
                            {editedContent.length} characters
                        </div>
                    </div>

                    {/* Save Button */}
                    <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(0, 0, 0, 0.1)', background: 'rgba(0, 0, 0, 0.02)' }}>
                        <button
                            onClick={handleSave}
                            disabled={!hasChanges || saving}
                            style={{
                                width: '100%',
                                padding: '14px 24px',
                                background: hasChanges ? '#7C3AED' : '#E5E5E5',
                                color: hasChanges ? '#FFFFFF' : '#999999',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
                                fontSize: '14px',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                transition: 'all 0.2s',
                            }}
                        >
                            {saving ? (
                                <>
                                    <div
                                        style={{
                                            width: '14px',
                                            height: '14px',
                                            border: '2px solid #FFFFFF',
                                            borderTop: '2px solid transparent',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite',
                                        }}
                                    />
                                    Saving...
                                </>
                            ) : saveSuccess ? (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    Saved!
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </button>
                    </div>
                </div>

                {/* Queue Metadata */}
                <div
                    style={{
                        marginTop: '24px',
                        padding: '16px 20px',
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '8px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                    }}
                >
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#666666', textTransform: 'uppercase', marginBottom: '12px' }}>
                        Queue Details
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                        <div>
                            <div style={{ fontSize: '11px', color: '#999999' }}>Account</div>
                            <div style={{ fontSize: '13px', color: '#000000' }}>@{decodeAccountId(accountId || '')}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: '11px', color: '#999999' }}>Session ID</div>
                            <div style={{ fontSize: '13px', color: '#000000' }}>{comment.sessionId}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: '11px', color: '#999999' }}>Queue Item ID</div>
                            <div style={{ fontSize: '13px', color: '#000000', fontFamily: 'monospace' }}>{comment.id}</div>
                        </div>
                    </div>
                </div>
            </div>

            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
        </div>
        </>
    );
};

export default QueueItemPage;
